import { add } from "@pentacode/openapi";
import { Localized } from "./localized";
import { AbsenceStatus, AvailabilityStatus, EmployeeStatus, EmploymentType, TimeEntryType, } from "./model";
import { getIntervalDuration, hour, overlap } from "./time";
import { clamp, getNextDay, getPrevDay, parseDateString, parseTimes, toDateString } from "./utils";
const availabilityTypeScores = {
    [AvailabilityStatus.Preferred]: 1,
    [AvailabilityStatus.Available]: 0.66,
    neutral: 0.33,
    [AvailabilityStatus.Unpreferred]: 0,
};
function getDynamicAvailability(employee, entry, entries, timeBalance, maxWorkingHoursPerDay, minRestingHours) {
    const log = false;
    const employeeEntries = entries.filter((otherEntry) => {
        return !otherEntry.deleted && otherEntry.employeeId === employee.id;
    });
    // Is not overlapping with another entry
    const overlapping = employeeEntries.find((otherEntry) => otherEntry.overlapsWith(entry));
    if (overlapping) {
        log &&
            console.log("employee is already assigned", overlapping.date, overlapping.startPlanned, overlapping.endPlanned, overlapping);
        return false;
    }
    const shiftDuration = entry.durationPlanned;
    // Max hours per week are 50 or 150% of nominal hours, whichever is lower
    // For helpers, it is the nominal hours
    // TODO: this will not work if range is not 1 week
    const isMarginal = employee.getContractForDate(entry.date)?.employmentType === EmploymentType.Marginal;
    const maxWeeklyHours = isMarginal ? timeBalance.nominal : Math.min(50, timeBalance.nominal * 1.5);
    // Is not exceeding the max hours per week
    if (timeBalance.actual + shiftDuration > maxWeeklyHours) {
        log && console.log("employee is exceeding max hours per week");
        return false;
    }
    // Is not exceeding the max hours per day
    const assignedDuration = employeeEntries.reduce((total, otherEntry) => {
        if (otherEntry.date === entry.date) {
            return total + otherEntry.durationPlanned;
        }
        return total;
    }, 0);
    if (assignedDuration + shiftDuration > maxWorkingHoursPerDay) {
        log && console.log("employee is exceeding max hours per day");
        return false;
    }
    // Entry is too close to entries on the previous day
    const prevDay = getPrevDay(entry.date);
    if (employeeEntries.some((otherEntry) => {
        if (otherEntry.date !== prevDay || !otherEntry.endPlanned || !entry.startPlanned) {
            return false;
        }
        return entry.startPlanned?.getTime() - otherEntry.endPlanned?.getTime() < minRestingHours * hour;
    })) {
        log && console.log("employee has too little rest time");
        return false;
    }
    // Entry is too close to entries on the next day
    const nextDay = getNextDay(entry.date);
    if (employeeEntries.some((otherEntry) => {
        if (otherEntry.date !== nextDay || !entry.endPlanned || !otherEntry.startPlanned) {
            return false;
        }
        return otherEntry.startPlanned?.getTime() - entry.endPlanned?.getTime() < minRestingHours * hour;
    })) {
        log && console.log("employee has too little rest time");
        return false;
    }
    return true;
}
function getAvailabilityInterval(availability) {
    if (!availability.date) {
        return null;
    }
    const [start, end] = parseTimes(availability.date, availability.start || "0:00", availability.end || "24:00");
    if (!start || !end) {
        return null;
    }
    return [start, end];
}
function getAvailabilityEntryOverlap(entry, availability) {
    // TODO: Entries can end on the next day, so maybe this is not sufficient
    if (availability.date !== entry.date) {
        return 0; // not on the same date, no overlap
    }
    const interval = getAvailabilityInterval(availability);
    const plannedInterval = entry.planned;
    if (!interval || !plannedInterval) {
        return 0; // no interval or planned interval, no overlap
    }
    return overlap(plannedInterval, [interval]);
}
export function isEmployeeAvailable({ employee, timeBalance }, entry, { entries, absences, departments, availabilities, company }) {
    const log = false;
    log &&
        console.log("checking employee ", employee.name, entry.position?.name, entry.date, entry.startPlanned, entry.endPlanned, entry);
    // Has any positions of department
    if (departments && !employee.positions.some((position) => departments?.includes(position.departmentId))) {
        log && console.log("employee is not working in this department");
        return false;
    }
    // Is active
    if (employee.status !== EmployeeStatus.Active) {
        log && console.log("employee is not active");
        return false;
    }
    // Has position of entry
    if (!employee.positions.some((position) => entry.positionId === position.id)) {
        log && console.log("employee is not qualified");
        return false;
    }
    // Entry has valid date
    const date = parseDateString(entry.date);
    if (!date) {
        log && console.log("entry has invalid date");
        return false;
    }
    // Has contract
    const contract = employee.getContractForDate(entry.date);
    if (!contract || contract.blocked === true) {
        log && console.log("employee does not have a valid contract");
        return false;
    }
    // is within contractual workdays
    if (contract.fixedWorkDays) {
        const hoursPerDayIdx = (date.getDay() + 6) % 7; // date is Sun - Sat, hoursPerDay is Mon - Sun
        const hours = contract.hoursPerDay?.[hoursPerDayIdx] || 0;
        if (hours === 0) {
            log && console.log("employee is not working on this day");
            return false;
        }
    }
    // Employee is available
    if (availabilities.some((availability) => availability.status === AvailabilityStatus.Unavailable &&
        availability.employeeId === employee.id &&
        getAvailabilityEntryOverlap(entry, availability) > 0)) {
        log && console.log("employee is unavailable");
        return false;
    }
    if (absences.some((absence) => absence.employeeId === employee.id &&
        [AbsenceStatus.Approved, AbsenceStatus.Inferred].includes(absence.status) &&
        absence.start <= entry.date &&
        absence.end > entry.date)) {
        log && console.log("employee is absent");
        return false;
    }
    const freeDays = entries.filter((otherEntry) => !otherEntry.deleted && otherEntry.employeeId === employee.id && otherEntry.type === TimeEntryType.Free);
    if (freeDays.some((freeDay) => freeDay.date === entry.date)) {
        log && console.log("employee is on free day");
        return false;
    }
    const { maxWorkingHoursPerDay, minRestingHours } = Localized[company.country];
    if (!getDynamicAvailability(employee, entry, entries, timeBalance, maxWorkingHoursPerDay, minRestingHours)) {
        return false;
    }
    return true;
}
function getAvailabilityScore(employeeId, entry, availabilites) {
    const plannedInterval = entry.planned;
    if (!plannedInterval) {
        return 0; // no planned interval, no score
    }
    let filledTimeFraction = 0;
    let availabilityScore = 0;
    for (const availability of availabilites) {
        // TODO: we should group availabilities by employeeId in advance
        if (availability.employeeId !== employeeId || availability.status === AvailabilityStatus.Unavailable) {
            continue; // not relevant availability
        }
        const hours = getAvailabilityEntryOverlap(entry, availability);
        if (hours <= 0) {
            continue; // no overlap, no score
        }
        // TODO: Availabilities for the entire day seem to the ones without a start and end time. In those cases we could always set the timeFraction to 1
        const timeFraction = hours / getIntervalDuration(entry.planned);
        filledTimeFraction += timeFraction;
        const scoreMultiplier = availabilityTypeScores[availability.status];
        availabilityScore += timeFraction * scoreMultiplier;
    }
    const remainingTimeFraction = 1 - filledTimeFraction;
    availabilityScore += remainingTimeFraction * availabilityTypeScores.neutral;
    return clamp(availabilityScore, 0, 1);
}
function getEmployeeScore(entry, empData, context) {
    const scores = {
        reduceAccumulatedOvertime: 0,
        matchNominalHours: 0,
        considerAvailabilities: 0,
    };
    // it matters to include the current entry in the time balance calculation.
    // This is because there might be different contracts between employees,
    // resulting in a different score for the same duration.
    const timeBalanceAfterShift = empData.timeBalance.actual + entry.durationPlanned;
    // score if the employee has deviating balance carry
    // At the start, this will be lower than the minimum.
    // It will only become a relevant criteria when the employee has less than 50% nominal minus hours
    // The more plus hours the employee has, the less incentivized they are to take on more work
    // Employees with more than 50% nominal surplus will rarely be assigned until their balance has adjusted or other employees match their balance.
    const maxOvertimeDeviation = 0.5 * empData.timeBalance.nominal; // has to work between 50% and 150% of the nominal hours
    const finalBalance = empData.timeBalance.balance + timeBalanceAfterShift; // balance after the time range and all current assignments, including the new entry
    const overtimeClamped = clamp(finalBalance, -maxOvertimeDeviation, maxOvertimeDeviation);
    const overtimeFraction = (overtimeClamped + maxOvertimeDeviation) / (2 * maxOvertimeDeviation); // remap deviation to 0-1 range
    scores.reduceAccumulatedOvertime = 1 - overtimeFraction; // invert so negative balance is highest score
    // score for matching the nominal hours
    // we need this in addition to `reduceAccumulatedOvertime` in case two employees have a huge time deficit/surplus, where
    // we still need to make sure available shifts are equally distributed between them.
    const actualFraction = timeBalanceAfterShift / empData.timeBalance.nominal; // can be from 0 to 1.5
    const clampedFraction = clamp(actualFraction - 0.5, 0, 1); // look at 0.5 to 1.5, below 0.5 will always be highest score
    scores.matchNominalHours = 1 - clampedFraction; // invert for 0 to be max score, 1 to be min score
    // score for availabilities
    scores.considerAvailabilities = getAvailabilityScore(empData.employee.id, entry, context.availabilities);
    return scores;
}
// sums up scores based on the weights
function applyWeights(scores, weights) {
    const final = new Map();
    for (const [employeeId, score] of scores.entries()) {
        final.set(employeeId, Object.entries(score).reduce((total, [key, value]) => total + value * weights[key], 0));
    }
    return final;
}
function findEmployee(entry, available, context) {
    // score every employee
    const scores = available.reduce((all, emp) => all.set(emp.employee.id, getEmployeeScore(entry, emp, context)), new Map());
    const finalScores = applyWeights(scores, context.weights);
    // return the employee with the best score
    available.sort((a, b) => finalScores.get(b.employee.id) - finalScores.get(a.employee.id));
    return available[0];
}
export function autoAssign(context) {
    const today = toDateString(new Date());
    const { maxWorkingHoursPerDay, minRestingHours } = Localized[context.company.country];
    // all entries we need to take into account when assigning, all non-deleted work entries
    const allEntries = context.entries.filter((entry) => !entry.deleted &&
        entry.type === TimeEntryType.Work &&
        // we already filter this here to not have to check irrelevant entries much earlier or later than the auto-assign range
        // TODO: it would make sense to take 1 day before and after the range into account, to catch overlaps and too little rest time
        entry.date >= context.range.from &&
        entry.date < context.range.to);
    // assign only relevant entries (unassigned, in the future, planned, in time range, department)
    const entriesToAssign = allEntries
        .filter((entry) => !entry.employeeId &&
        entry.date > today &&
        entry.startPlanned &&
        entry.endPlanned &&
        (!context.departments ||
            !entry.position?.departmentId ||
            context.departments.includes(entry.position?.departmentId)))
        .sort((a, b) => b.duration - a.duration);
    // create maps for available employees to time entries
    const availableEmployees = new Map();
    const availableEntries = new Map();
    for (const entry of entriesToAssign) {
        const availableEmployeeData = context.employees.filter((e) => {
            return isEmployeeAvailable(e, entry, context);
        });
        availableEmployees.set(entry.id, availableEmployeeData);
        for (const employeeData of availableEmployeeData) {
            if (!availableEntries.has(employeeData.employee.id)) {
                availableEntries.set(employeeData.employee.id, []);
            }
            availableEntries.get(employeeData.employee.id).push(entry);
        }
    }
    while (entriesToAssign.length) {
        // get entry with the least available employees
        // TODO: maybe we can improve the selection of the next entry.
        // if availableEmployees are the same length, we can still sort by duration like above
        // we can also try and assign earlier entries first. These entries would be assigned to the employee in most need of hours,
        // so we leave the employee open to get assigned a second later shift.
        entriesToAssign.sort((a, b) => availableEmployees.get(a.id).length - availableEmployees.get(b.id).length);
        const entry = entriesToAssign.shift();
        // find the best suited employee for this entry
        const employeeData = findEmployee(entry, availableEmployees.get(entry.id), context);
        if (employeeData) {
            // assign the employee, update time balance
            entry.employeeId = employeeData.employee.id;
            employeeData.timeBalance.actual = add(employeeData.timeBalance.actual, entry.duration);
            // update available entries to remove this entry and any that would be in conflict with it
            const employeeAvailableEntries = availableEntries.get(employeeData.employee.id);
            // update available entries
            const newAvailableEntries = employeeAvailableEntries.filter((otherEntry) => getDynamicAvailability(employeeData.employee, otherEntry, allEntries, employeeData.timeBalance, maxWorkingHoursPerDay, minRestingHours));
            availableEntries.set(employeeData.employee.id, newAvailableEntries);
            // update available employees for each removed entry
            employeeAvailableEntries
                .filter((otherEntry) => !newAvailableEntries.includes(otherEntry)) // only removed entries
                .forEach((otherEntry) => {
                const entryAvailableEmployees = availableEmployees.get(otherEntry.id);
                const newAvailableEmployees = entryAvailableEmployees?.filter((otherEmployeeData) => otherEmployeeData.employee.id !== employeeData.employee.id);
                newAvailableEmployees && availableEmployees.set(otherEntry.id, newAvailableEmployees);
            });
        }
    }
}
//# sourceMappingURL=auto-assign.js.map